import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowLeft, ArrowRight, Phone, MapPin, FileText, Download } from 'lucide-react';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';
import { getPostBySlug } from '../data/blog';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const Block = ({ block }) => {
  if (block.type === 'subheading') {
    return <h3 className="font-heading text-xl md:text-2xl font-medium text-primary-blue mt-8 mb-3">{block.content}</h3>;
  }
  if (block.type === 'list') {
    return (
      <ul className="list-disc pl-6 space-y-2 text-text-gray mb-5">
        {block.content.map((item, k) => (
          <li key={k} className="leading-relaxed">{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'highlight') {
    return (
      <div className="border-l-4 border-warm-accent bg-light-blue/20 rounded-r-xl p-5 my-6">
        <p className="text-text-dark leading-relaxed font-medium">{block.content}</p>
      </div>
    );
  }
  if (block.type === 'cta') {
    return (
      <div className="bg-primary-blue text-white rounded-2xl p-6 md:p-8 my-8">
        <div className="flex items-start gap-3 mb-5">
          <MapPin className="w-5 h-5 text-warm-accent mt-1 flex-shrink-0" />
          <p className="text-white/85 leading-relaxed">{block.content}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild className="bg-warm-accent hover:bg-white hover:text-primary-blue text-white rounded-full px-6 py-5 font-medium">
            <Link to="/contact" data-testid="blog-cta-contact" className="flex items-center gap-2">
              <Phone className="w-4 h-4" /> Prendre rendez-vous
            </Link>
          </Button>
          <Button asChild variant="outline" className="border-white/40 text-white hover:bg-white hover:text-primary-blue rounded-full px-6 py-5 font-medium bg-transparent">
            <Link to="/a-propos" className="flex items-center gap-2">
              En savoir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    );
  }
  if (block.type === 'table') {
    return (
      <div className="my-6 overflow-x-auto rounded-2xl border border-border-soft">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="bg-primary-blue text-white">
              {block.headers.map((h, i) => (
                <th key={i} className="px-4 py-3 font-medium font-heading">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r} className={r % 2 === 1 ? 'bg-light-blue/10' : 'bg-white'}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={`px-4 py-3 align-top border-t border-border-soft ${c === 0 ? 'font-semibold text-primary-blue whitespace-nowrap' : 'text-text-gray'}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === 'download') {
    return (
      <a
        href={block.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="blog-download"
        className="group flex items-center gap-4 bg-white border border-border-soft rounded-2xl p-5 my-6 hover:border-warm-accent hover:shadow-md transition-all"
      >
        <div className="w-12 h-12 rounded-xl bg-light-blue/40 flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6 text-primary-blue" />
        </div>
        <div className="flex-1">
          <p className="font-heading font-medium text-primary-blue">{block.label}</p>
          {block.note && <p className="text-sm text-text-gray">{block.note}</p>}
        </div>
        <Download className="w-5 h-5 text-warm-accent flex-shrink-0" />
      </a>
    );
  }
  if (block.type === 'image') {
    return (
      <figure className="my-8">
        <div className="rounded-2xl overflow-hidden border border-border-soft bg-white p-3 md:p-4">
          <img src={block.src} alt={block.alt || ''} className="w-full h-auto" loading="lazy" />
        </div>
        {block.caption && (
          <figcaption className="text-center text-sm text-text-gray mt-3 italic">{block.caption}</figcaption>
        )}
      </figure>
    );
  }
  return <p className="text-text-gray leading-relaxed text-justify mb-4">{block.content}</p>;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const faqJsonLd = post.faq && post.faq.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title={`${post.title} | MMD CONSEIL`}
        description={post.metaDescription}
        url={getFullUrl(`/blog/${post.slug}`)}
        image={post.image}
        type="article"
      />
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        </Helmet>
      )}

      {/* Hero */}
      <section className="bg-primary-blue text-white pt-28 md:pt-36 pb-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-warm-accent mb-4">{post.category}</p>
          <h1 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1] mb-6">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatDate(post.date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.readingTime}</span>
            <span>Par {post.author}</span>
          </div>
        </div>
      </section>

      {/* Image de couverture */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 -mt-10">
        <div className="rounded-3xl overflow-hidden shadow-xl">
          <img src={post.image} alt={post.title} className="w-full h-[280px] md:h-[420px] object-cover" />
        </div>
      </div>

      {/* Contenu */}
      <article className="max-w-3xl mx-auto px-4 md:px-8 py-14">
        {/* Réponse rapide */}
        <div className="bg-white border border-border-soft rounded-2xl p-6 md:p-8 shadow-sm mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-warm-accent mb-3">Réponse rapide</p>
          <p className="text-text-dark leading-relaxed text-justify">{post.excerpt}</p>
          {post.updatedNote && <p className="text-text-gray text-sm mt-4 italic">{post.updatedNote}</p>}
        </div>

        {/* Sommaire */}
        <nav className="bg-light-blue/20 rounded-2xl p-6 mb-12" aria-label="Sommaire">
          <p className="font-heading text-lg font-medium text-primary-blue mb-4">Sommaire</p>
          <ol className="space-y-2 list-decimal pl-5">
            {post.sommaire.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="text-secondary-blue hover:text-warm-accent transition-colors">
                  {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        {post.sections.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-28 mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-primary-blue mb-5">{section.heading}</h2>
            {section.blocks.map((block, j) => (
              <Block key={j} block={block} />
            ))}
          </section>
        ))}

        {/* FAQ */}
        {post.faq && post.faq.length > 0 && (
        <section id="faq" className="scroll-mt-28 mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-light text-primary-blue mb-6">Questions fréquentes</h2>
          <div className="divide-y divide-border-soft border-y border-border-soft">
            {post.faq.map((f, i) => (
              <div key={i} className="py-5" data-testid={`blog-faq-${i}`}>
                <h3 className="font-heading text-lg font-medium text-primary-blue mb-2">{f.q}</h3>
                <p className="text-text-gray leading-relaxed text-justify">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
        )}

        {/* Conclusion */}
        {post.conclusion && (
          <section className="mb-4">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-primary-blue mb-5">{post.conclusion.heading}</h2>
            {post.conclusion.blocks.map((block, j) => (
              <Block key={j} block={block} />
            ))}
          </section>
        )}

        {post.updatedNote && <p className="text-text-gray text-sm italic mt-8">{post.updatedNote}</p>}

        <div className="mt-10 pt-8 border-t border-border-soft">
          <Link to="/blog" className="inline-flex items-center gap-2 font-medium text-primary-blue hover:text-warm-accent">
            <ArrowLeft className="w-4 h-4" /> Retour à tous les articles
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;
